import { createTheme, PaletteMode, Theme } from '@mui/material';
import './theme.d';

const getDesignTokens = (mode: PaletteMode) => ({
  palette: {
    mode,
    primary: {
      main: '#2161c6',
    },
    secondary: {
      main: '#667FA6',
    },

    success: mode === 'light'
      ? { main: '#4CAF50', lighter: '#E8F5E9' }
      : { main: '#66BB6A', lighter: 'rgba(102, 187, 106, 0.18)' },
    error: mode === 'light'
      ? { main: '#F44336', lighter: '#FFEBEE' }
      : { main: '#EF5350', lighter: 'rgba(239, 83, 80, 0.18)' },
    warning: mode === 'light'
      ? { main: '#FF9800', lighter: '#FFF3E0' }
      : { main: '#FFA726', lighter: 'rgba(255, 167, 38, 0.18)' },
    info: mode === 'light'
      ? { main: '#2196F3', lighter: '#E3F2FD' }
      : { main: '#42A5F5', lighter: 'rgba(66, 165, 245, 0.18)' },

    domain: mode === 'light'
      ? {
          users: '#1565C0',
          verified: '#2E7D32',
          serviceCards: '#E65100',
          permits: '#1976D2',
          relationships: '#6A1B9A',
          courses: '#AD1457',
          attentionCalls: '#C62828',
          incidents: '#BF360C',
          supportTickets: '#37474F',
          inspections: '#00838F',
        }
      : {
          users: '#64B5F6',
          verified: '#81C784',
          serviceCards: '#FFB74D',
          permits: '#90CAF9',
          relationships: '#CE93D8',
          courses: '#F48FB1',
          attentionCalls: '#EF9A9A',
          incidents: '#FFAB91',
          supportTickets: '#B0BEC5',
          inspections: '#80DEEA',
        },

    // Third-party brand colors (same in both modes — a brand hue must render
    // faithfully regardless of light/dark). Tokenized so the literal never
    // gets copied inline on the "contact via WhatsApp" icon links.
    brand: {
      whatsapp: '#25D366',
      whatsappHover: '#1DA851',
    },

    taskType: mode === 'light'
      ? {
          trabajo_altura: '#e53935',
          espacio_confinado: '#8e24aa',
          trabajo_electrico: '#fdd835',
          trabajo_caliente: '#ff6d00',
          excavacion: '#795548',
          izaje: '#546e7a',
          maquinaria: '#37474f',
          herramientas_alto_riesgo: '#d84315',
          sustancias_quimicas: '#00897b',
          riesgo_incendio: '#c62828',
          agentes_fisicos: '#1565c0',
          agentes_biologicos: '#2e7d32',
        }
      : {
          trabajo_altura: '#ef5350',
          espacio_confinado: '#ba68c8',
          trabajo_electrico: '#fff176',
          trabajo_caliente: '#ffa726',
          excavacion: '#bcaaa4',
          izaje: '#90a4ae',
          maquinaria: '#90a4ae',
          herramientas_alto_riesgo: '#ff8a65',
          sustancias_quimicas: '#4db6ac',
          riesgo_incendio: '#ef5350',
          agentes_fisicos: '#42a5f5',
          agentes_biologicos: '#66bb6a',
        },

    background: mode === 'light'
      ? {
          default: '#F2F6FA',
          primary: '#2161c6',
          paper: '#FFFFFF',
          subtle: '#F5F5F5',
          sidebar: '#182D9D',
          sidebartext: '#C8D5F5',
          sidebaritemtext: '#C8D5F5',
          sidebarSelectedBg: '#F2F6FA',
          sidebarSelectedText: '#182D9D',
          buttonBg: '#2161c6',
          buttonText: '#fff',
          chipSuccess: '#4CAF50',
          chipWarning: '#FF9800',
          chipError: '#F44336',
          chipInfo: '#2196F3',
          tableRowHoverDefault: 'rgba(0, 0, 0, 0.04)',
          tableRowHoverError: 'rgba(244, 67, 54, 0.08)',
          tableRowHoverWarning: 'rgba(255, 152, 0, 0.08)',
        }
      : {
          // Dark mode: navy-tinted page background, slightly lighter navy for
          // sidebar + cards (matching), bright blue accent for sidebar text/
          // icons. Selected sidebar state uses the same bg as the rail so the
          // active item shows via the text color, not a contrasting rectangle.
          default: '#151A30',
          primary: '#0976ED',
          paper: '#1A1F38',
          subtle: '#1F2440',
          sidebar: '#1A1F38',
          sidebartext: '#0976ED',
          // Sidebar item LABELS render in white in dark mode for legibility,
          // while ICONS keep `sidebartext` (the contrast blue).
          sidebaritemtext: '#FFFFFF',
          sidebarSelectedBg: '#1A1F38',
          sidebarSelectedText: '#0976ED',
          buttonBg: '#0976ED',
          buttonText: '#fff',
          chipSuccess: '#66BB6A',
          chipWarning: '#FFA726',
          chipError: '#EF5350',
          chipInfo: '#42A5F5',
          tableRowHoverDefault: 'rgba(9, 118, 237, 0.10)',
          tableRowHoverError: 'rgba(239, 83, 80, 0.12)',
          tableRowHoverWarning: 'rgba(255, 167, 38, 0.12)',
        },
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    // Small-screen polish only. Every rule below is wrapped in a
    // `breakpoints.down('sm')` (< 600px) media query so 640px+ and desktop
    // are intentionally untouched.
    MuiButton: {
      styleOverrides: {
        // Platform-wide button casing: sentence case (not MUI's default
        // UPPERCASE). Set once here so no button needs its own
        // `textTransform: 'none'` — change this one line to flip the whole app.
        root: ({ theme }: { theme: Theme }) => ({
          textTransform: 'none' as const,
          borderRadius: 100,
          paddingTop: 8,
          paddingBottom: 8,
          paddingLeft: 24,
          paddingRight: 24,
          // Keep action labels on a single line and shrink padding/font to fit
          // narrow viewports instead of wrapping the label onto two rows.
          [theme.breakpoints.down('sm')]: {
            whiteSpace: 'nowrap',
            fontSize: '0.78rem',
            paddingLeft: theme.spacing(1.25),
            paddingRight: theme.spacing(1.25),
            minWidth: 'auto',
            '& .MuiButton-startIcon': { marginRight: theme.spacing(0.5) },
            '& .MuiButton-endIcon': { marginLeft: theme.spacing(0.5) },
          },
        }),
      },
    },
    // Tabs and toggle buttons default to UPPERCASE too — match the
    // sentence-case decision platform-wide so per-file overrides aren't needed.
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none' as const },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: { textTransform: 'none' as const },
      },
    },
    MuiTabs: {
      styleOverrides: {
        // Let the tab strip scroll horizontally rather than squashing or
        // wrapping labels. Scoped to the Tabs scroller so only the tabs
        // scroll — not the page.
        root: ({ theme }: { theme: Theme }) => ({
          [theme.breakpoints.down('sm')]: {
            '& .MuiTabs-scroller': {
              overflowX: 'auto !important',
              scrollbarWidth: 'none',
            },
            '& .MuiTabs-scroller::-webkit-scrollbar': { display: 'none' },
            '& .MuiTabs-flexContainer': { width: 'max-content' },
          },
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        // On phones stack the action buttons vertically (primary on top via
        // column-reverse, since it's the last child) and let them stretch to
        // full width, instead of cramming two/three buttons on one row.
        // Desktop keeps the default right-aligned row.
        root: ({ theme }: { theme: Theme }) => ({
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column-reverse',
            alignItems: 'stretch',
            gap: theme.spacing(1),
            // Kill the default horizontal sibling margin so the gap is even.
            '& > :not(style) ~ :not(style)': { marginLeft: 0 },
          },
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        // On phones make form controls fill the dialog width so inputs never
        // render as narrow inline boxes. The descendant selector outranks a
        // plain inline `sx={{ width: N }}`, so fixed-width fields go full
        // width too. Desktop is untouched.
        root: ({ theme }: { theme: Theme }) => ({
          [theme.breakpoints.down('sm')]: {
            '& .MuiFormControl-root, & .MuiTextField-root, & .MuiAutocomplete-root, & .MuiToggleButtonGroup-root': {
              width: '100%',
            },
          },
        }),
      },
    },
  },
});

export const getAppTheme = (mode: PaletteMode) => createTheme(getDesignTokens(mode));

// Export default theme config for merging with company themes
export const getDefaultThemeConfig = (mode: PaletteMode) => getDesignTokens(mode);
